import { ReactNode, useEffect, useState } from 'react';
import { GridDashboardItem } from '../../types/insight-custom-dashboard';
import { DashboardColumn } from '@g17eco/molecules/dashboard';
import { SimpleTooltip } from '@g17eco/molecules/simple-tooltip';
import { convertImgToBase64URL } from '@features/report-output/document-structure';
import { getImageBase64FromSVGBase64, isSvgUrl } from '@utils/image';
import { IntegrationData } from '@g17eco/types/app-integration';
import { PADDING, getTitle, getItemIcon } from './gridItemUtils';

export interface GridItemWrapperProps {
  item: GridDashboardItem;
  isEditing: boolean;
  integrationsData?: IntegrationData;
  openHistoryModal?: () => void;
  children: ReactNode;
}

type TitleGetProps = Omit<GridItemWrapperProps, 'children' | 'isEditing'>;

const getItemTitle = ({ item, openHistoryModal, integrationsData }: TitleGetProps) => {
  const title = getTitle({ item, integrationsData });
  if (!title) {
    return undefined;
  }

  if (openHistoryModal) {
    return (
      <SimpleTooltip text={title}>
        <div
          style={{ cursor: 'pointer' }}
          className='d-inline-block dashboard-title'
          onClick={() => openHistoryModal()}
        >
          <p className='m-0'>{title}</p>
        </div>
      </SimpleTooltip>
    );
  }
  return (
    <SimpleTooltip text={title}>
      <div className='d-inline-block dashboard-title'>
        <p className='m-0'>{title}</p>
      </div>
    </SimpleTooltip>
  );
};

export const GridItemWrapper = ({ item, children, isEditing, openHistoryModal, integrationsData }: GridItemWrapperProps) => {
  const title = getItemTitle({ item, openHistoryModal, integrationsData });
  const [icon, setIcon] = useState<string | undefined>();

  // Serving base64 instead of svg to support exporting dashboard
  // TODO: Serve base64 icon directly, or find another way to do this efficiently
  useEffect(() => {
    const itemIcon = getItemIcon(item);
    if (!itemIcon || icon) {
      return;
    }

    if (!isSvgUrl(itemIcon)) {
      setIcon(itemIcon);
      return;
    }

    const getConvertedBase64 = async (itemIcon: string) => {
      const svgBase64 = await convertImgToBase64URL(itemIcon);
      return getImageBase64FromSVGBase64({ imgBase64: svgBase64, removeBase64Prefix: false });
    };
    // This is very inefficient and it increased the size of the exported PDF dramatically
    // Need to optimize this
    getConvertedBase64(itemIcon).then((icon) => setIcon(icon));
  }, [item, icon]);

  return (
    <DashboardColumn
      title={title}
      padding={PADDING}
      headerIcon={icon ? <img data-testid={'widget-icon'} alt={'icon'} src={icon} width={40} height={40} /> : null}
      className={`h-100 ${isEditing ? 'border-0' : ''}`}
    >
      <div className='h-100 d-flex flex-column justify-content-between position-relative'>{children}</div>
    </DashboardColumn>
  );
};
